// Lightweight calendar helpers shared between driver and admin calendar views.
// All dates here are JS Date objects; events use naive ISO strings from the
// backend (YYYY-MM-DDTHH:MM:SS) — parsed with `new Date(iso)` in local time.

export const EVENT_COLORS = {
  sdrive_ride: {
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/50",
    dot: "bg-emerald-500",
    text: "text-emerald-300",
    label: "S.DRIVE",
  },
  personal_ride: {
    bg: "bg-sky-500/15",
    border: "border-sky-500/50",
    dot: "bg-sky-500",
    text: "text-sky-300",
    label: "Personnelle",
  },
  // Phase 1 — actual personal rides from `db.driver_personal_rides` shown in
  // the unified calendar. Distinct violet so drivers can tell them apart from
  // free-form personal events at a glance.
  driver_personal_ride: {
    bg: "bg-violet-500/15",
    border: "border-violet-500/50",
    dot: "bg-violet-500",
    text: "text-violet-300",
    label: "Mes courses",
  },
  unavailability: {
    bg: "bg-rose-500/15",
    border: "border-rose-500/60",
    dot: "bg-rose-500",
    text: "text-rose-300",
    label: "Indisponible",
  },
};

export const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const MONTH_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Monday-first week: return the Monday of the week containing `d`.
export function startOfWeek(d) {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // 0 = Mon .. 6 = Sun
  x.setDate(x.getDate() - day);
  return x;
}

export function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}

export function formatRange(startIso, endIso) {
  const s = parseEventDate(startIso);
  const e = parseEventDate(endIso);
  if (!s || !e) return "";
  const f = (d) => d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (isSameDay(s, e)) return `${f(s)} → ${f(e)}`;
  const df = (d) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  return `${df(s)} ${f(s)} → ${df(e)} ${f(e)}`;
}

export function formatFullDate(d) {
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

// Convert a Date to the "YYYY-MM-DDTHH:MM" value that <input type="datetime-local"> expects.
export function toLocalInput(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert the value of <input type="datetime-local"> ("YYYY-MM-DDTHH:MM")
// back into a naive ISO string the backend accepts.
export function fromLocalInput(value) {
  if (!value) return "";
  return value.length === 16 ? `${value}:00` : value;
}

export function eventsOverlappingDay(events, day) {
  const ds = startOfDay(day).getTime();
  const de = addDays(day, 1).getTime();
  return events.filter((ev) => {
    const s = parseEventDate(ev.start_datetime)?.getTime();
    const e = parseEventDate(ev.end_datetime)?.getTime();
    if (!s || !e) return false;
    return s < de && e > ds;
  });
}
